import { useI18n } from "i18n";
import { useContext, useEffect, useState } from "react";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { Divider } from "@remo-co/ui-core/src/components/Divider";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Container } from "@remo-co/ui-core/src/components/Container";
import {
  BROWSER_NAMES,
  OS_NAMES,
  SUPPORT_STATUS,
} from "modules/systemCheck/constants";
import { PermissionState, selectIsMobile } from "modules/deviceInfo";
import { GeartestContext } from "modules/micCamCheck/types";
import { useSelector } from "react-redux";
import { BrowserSupport } from "modules/systemCheck/types";
import classnames from "classnames";
import { StatusIcon } from "modules/systemCheck/components/StatusIcon";
import { BrowserDetection } from "../BrowserDetection";
import { FirewallDetection } from "../FirewallDetection";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { OperatingSystemDetection } from "../OperatingSystemDetection";
import { AdBlockDetection } from "../AdBlockDetection/AdBlockDetection";
import { VPNDetection } from "../VPNDetection";
import { ReachabilityTestResult } from "../types";
import { FirebaseDetectionError } from "../FirebaseDetectionError";
import { useStyles } from "./styles";
import { DailyDetectionError } from "../DailyDetectionError";
import { AVDevicesCheck } from "../AVDevicesCheck";
import SuccessSVG from "../../assets/success.svg";

interface Props {
  audioPermissionState: PermissionState;
  videoPermissionState: PermissionState;
  systemStatus: SUPPORT_STATUS;
  requiredSystemStatus: SUPPORT_STATUS;
  systemSettings: {
    osSupported: boolean;
    browserSupport: BrowserSupport;
    osName: OS_NAMES;
    browserName: BROWSER_NAMES;
  };
  allChecksCompleted: boolean;
  isLoading: boolean;
  isRequiredChecksLoading: boolean;
  isInternetConnectionOK: boolean;
  isFirebaseAccessible: boolean;
  isFirestoreAccessible: boolean;
  isFirebaseStorageAccessible: boolean;
  isDailyAccessible: boolean;
  isAdBlockEnabled: boolean;
  isVPNDetected: boolean;
  reachabilityResult: ReachabilityTestResult;
  disableVPNCheck: boolean;
  context: GeartestContext;
}

const Result = ({
  audioPermissionState,
  videoPermissionState,
  systemStatus,
  requiredSystemStatus,
  systemSettings,
  allChecksCompleted,
  isLoading,
  isRequiredChecksLoading,
  isInternetConnectionOK,
  isFirebaseAccessible,
  isFirestoreAccessible,
  isFirebaseStorageAccessible,
  isDailyAccessible,
  isAdBlockEnabled,
  isVPNDetected,
  reachabilityResult,
  disableVPNCheck,
  context,
}: Props): JSX.Element => {
  const { track } = useContext(TRACKING_CONTEXT);
  const [showMore, setShowMore] = useState(false);
  const isMobile = useSelector(selectIsMobile);
  const { t } = useI18n(["common", "micCamCheck"]);
  const styles = useStyles({ isMobile });

  const { isAnyDeviceMissing } = useDeviceDetection();

  const hasAnyNetworkCheckFailed =
    !isFirebaseStorageAccessible ||
    Object.values(reachabilityResult).some((result) => !result.isSuccessful);

  const displaySystemStatus = isLoading ? requiredSystemStatus : systemStatus;
  // can refine: tracking, showing errors, etc.
  const hasPermissionIssues =
    audioPermissionState !== PermissionState.GRANTED ||
    videoPermissionState !== PermissionState.GRANTED;
  const noOfRecommendations = [
    hasAnyNetworkCheckFailed,
    isAdBlockEnabled,
    isVPNDetected,
    systemSettings.browserSupport.status !== SUPPORT_STATUS.SUPPORTED,
    !systemSettings.osSupported,
  ].filter(Boolean).length;

  useEffect(() => {
    if (!isLoading) {
      track(
        allChecksCompleted
          ? Events.SYSTEM_CHECK_SECONDARY
          : Events.SYSTEM_CHECK_PRIMARY,
        {
          browser_permissions: hasPermissionIssues ? "fail" : "pass",
          devices: !systemSettings.osSupported ? "fail" : "pass",
          network: hasAnyNetworkCheckFailed ? "fail" : "pass",
          speed: !isInternetConnectionOK ? "fail" : "pass",
          browser:
            systemSettings.browserSupport.status !== SUPPORT_STATUS.SUPPORTED
              ? "fail"
              : "pass",
          firebase: !isFirebaseAccessible ? "fail" : "pass",
          firestore: !isFirestoreAccessible ? "fail" : "pass",
          // eslint-disable-next-line no-nested-ternary
          vpn: disableVPNCheck ? undefined : isVPNDetected ? "fail" : "pass",
          adblock: isAdBlockEnabled ? "fail" : "pass",
          daily: !isDailyAccessible ? "fail" : "pass",
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allChecksCompleted]);

  const isPreEvent = context === GeartestContext.preEvent;
  const showSystemCheckError =
    hasPermissionIssues ||
    isAnyDeviceMissing ||
    displaySystemStatus === SUPPORT_STATUS.UNSUPPORTED;

  const showMoreButton =
    !showMore && !isLoading && (!isPreEvent || !!noOfRecommendations);

  const showSuccessEmptyState = !showSystemCheckError && !showMore;

  return (
    <div id="systemCheckWrapper" className={styles.wrapper}>
      <div className={styles.results}>
        {!isRequiredChecksLoading && showSuccessEmptyState && (
          <Container
            flex
            alignItems="center"
            justifyContent="center"
            className={classnames(styles.supportedStatus)}
          >
            <Container flex alignItems="center">
              <StatusIcon status={SUPPORT_STATUS.SUPPORTED} />

              <Typography
                variant="h5"
                className={styles.successfulStatusText}
                data-testid="system-check-message"
                tabIndex={0}
              >
                {displaySystemStatus === SUPPORT_STATUS.SUPPORTED &&
                  t("micCamCheck:system.check.passed")}
                {displaySystemStatus === SUPPORT_STATUS.PARTIALLY_SUPPORTED &&
                  t("micCamCheck:system.check.passed.with.recommendations")}
              </Typography>
            </Container>
            <Typography variant="body1" className={styles.subheading}>
              {t("micCamCheck:geartest.successful")}
            </Typography>
            <img alt={t("micCamCheck:geartest.successful")} src={SuccessSVG} />
          </Container>
        )}

        {!isRequiredChecksLoading && !showSuccessEmptyState && (
          <Container
            flex
            alignItems="center"
            justifyContent="center"
            className={styles.status}
          >
            <Container flex alignItems="center">
              <StatusIcon
                status={
                  hasPermissionIssues || isAnyDeviceMissing
                    ? SUPPORT_STATUS.UNSUPPORTED
                    : displaySystemStatus
                }
              />
              {showSystemCheckError ? (
                <Typography
                  variant="h5"
                  className={styles.failedStatusText}
                  data-testid="system-check-message"
                  tabIndex={0}
                >
                  {t("micCamCheck:system.check.detected.issues")}
                </Typography>
              ) : (
                <Typography
                  variant="h5"
                  className={styles.successfulStatusText}
                  data-testid="system-check-message"
                  tabIndex={0}
                >
                  {displaySystemStatus === SUPPORT_STATUS.SUPPORTED &&
                    t("micCamCheck:system.check.passed")}
                  {displaySystemStatus === SUPPORT_STATUS.PARTIALLY_SUPPORTED &&
                    t("micCamCheck:system.check.passed.with.recommendations")}
                </Typography>
              )}
            </Container>
          </Container>
        )}
        {isLoading && (
          <>
            {!isRequiredChecksLoading && <Divider />}
            <Container
              flex
              alignItems="center"
              justifyContent="center"
              className={styles.loading}
            >
              <Loader
                size={20}
                className={styles.loaderIcon}
                data-testid="progress"
              />
              <Typography
                variant="subtitle1"
                data-testid="running-system-check"
                tabIndex={0}
              >
                {isRequiredChecksLoading
                  ? t("micCamCheck:running.required.checks")
                  : t("micCamCheck:running.secondary.checks")}
              </Typography>
            </Container>
          </>
        )}
        <FirebaseDetectionError
          isFirebaseAccessible={isFirebaseAccessible}
          isFirestoreAccessible={isFirestoreAccessible}
          isRequiredChecksLoading={isRequiredChecksLoading}
          hasDivider
        />
        <DailyDetectionError
          isDailyAccessible={isDailyAccessible}
          isRequiredChecksLoading={isRequiredChecksLoading}
          hasDivider
        />
        <AVDevicesCheck
          videoPermissionState={videoPermissionState}
          audioPermissionState={audioPermissionState}
          osName={systemSettings.osName}
          browserName={systemSettings.browserName}
        />
        {showMore && (!isPreEvent || hasAnyNetworkCheckFailed) && (
          <FirewallDetection
            isLoading={isLoading}
            isFirebaseStorageAccessible={isFirebaseStorageAccessible}
            reachabilityResult={reachabilityResult}
          />
        )}
        {showMore && (!isPreEvent || isAdBlockEnabled) && (
          <AdBlockDetection
            isLoading={isLoading}
            isAdBlockEnabled={isAdBlockEnabled}
          />
        )}
        {showMore && !disableVPNCheck && (!isPreEvent || isVPNDetected) && (
          <VPNDetection isLoading={isLoading} isVPNDetected={isVPNDetected} />
        )}
        {showMore &&
          (!isPreEvent ||
            systemSettings.browserSupport.status !==
              SUPPORT_STATUS.SUPPORTED) && (
            <BrowserDetection
              browserSupport={systemSettings.browserSupport}
              browser={systemSettings.browserName ?? ""}
            />
          )}
        {showMore && (!isPreEvent || !systemSettings.osSupported) && (
          <OperatingSystemDetection
            os={systemSettings.osName ?? ""}
            isSupported={systemSettings.osSupported}
          />
        )}
        {showMoreButton && (
          <>
            <Divider />
            <Typography
              variant="body1"
              onKeyPress={({ key }) => {
                if (key === "Enter") {
                  setShowMore(true);
                }
              }}
              onClick={() => setShowMore(true)}
              className={styles.expand}
              data-testid="show-more"
              tabIndex={0}
              aria-label={
                noOfRecommendations
                  ? t("view.recommendations.accessibility")
                  : t("view.results.accessibility")
              }
            >
              {noOfRecommendations
                ? t("view.recommendations", {
                    number: noOfRecommendations,
                  })
                : t("view.results")}
            </Typography>
          </>
        )}
      </div>
    </div>
  );
};

export default Result;
